import { useCallback, useEffect, useState } from "react";
import { useBrainApi } from "../../api/brainApi";
import { Domain } from "../../api/types";
import {
  PageTitle,
  Panel,
  AlertBanner,
  inputClass,
  btnPrimary,
  btnSecondary,
} from "../../components/controlPlaneUi";

const EMPTY = { name: "", description: "", repos: "" };

const DomainsPage: React.FC = () => {
  const api = useBrainApi();
  const [domains, setDomains] = useState<Domain[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setDomains(await api.listDomains());
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "failed to load domains");
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    void load();
  }, [load]);

  const reset = () => {
    setForm(EMPTY);
    setEditId(null);
  };

  const startEdit = (d: Domain) => {
    setEditId(d.id);
    setForm({ name: d.name, description: d.description ?? "", repos: d.repos.join("\n") });
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    const payload = {
      name: form.name.trim(),
      description: form.description.trim() || undefined,
      repos: form.repos
        .split("\n")
        .map((r) => r.trim())
        .filter(Boolean),
    };
    try {
      if (editId) await api.updateDomain(editId, payload);
      else await api.createDomain(payload);
      reset();
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (d: Domain) => {
    if (!window.confirm(`Delete domain "${d.name}"? Its uploaded transcripts go too.`)) return;
    setError(null);
    try {
      await api.deleteDomain(d.id);
      if (editId === d.id) reset();
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "delete failed");
    }
  };

  return (
    <div>
      <PageTitle kind="Configuration" name="domains" />
      {error && <AlertBanner variant="error">{error}</AlertBanner>}

      <div className="grid gap-4 lg:grid-cols-[360px_1fr]">
        <Panel title={editId ? "Edit domain" : "New domain"}>
          <form onSubmit={submit} className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Name</label>
              <input
                className={inputClass}
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="acme-platform"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Description</label>
              <input
                className={inputClass}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="optional"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">
                Repositories <span className="text-slate-400">(owner/repo, one per line)</span>
              </label>
              <textarea
                className={`${inputClass} h-28 py-2`}
                value={form.repos}
                onChange={(e) => setForm({ ...form, repos: e.target.value })}
                placeholder={"digitalfamilyoffice/brain-looper\ndigitalfamilyoffice/some-other-repo"}
              />
            </div>
            <div className="flex gap-2">
              <button type="submit" className={btnPrimary} disabled={saving || !form.name.trim()}>
                {saving ? "Saving…" : editId ? "Update domain" : "Create domain"}
              </button>
              {editId && (
                <button type="button" className={btnSecondary} onClick={reset}>
                  Cancel
                </button>
              )}
            </div>
          </form>
        </Panel>

        <Panel title={`Domains (${domains.length})`}>
          {loading ? (
            <p className="text-sm text-slate-500">Loading…</p>
          ) : domains.length === 0 ? (
            <p className="text-sm text-slate-500">
              No domains yet. Create one to group repositories and receive transcripts.
            </p>
          ) : (
            <ul className="divide-y divide-[#262420]">
              {domains.map((d) => (
                <li key={d.id} className="py-3 first:pt-0">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-mono text-sm font-semibold text-[#E5E0D5]">{d.name}</div>
                      {d.description && (
                        <div className="text-xs text-slate-500">{d.description}</div>
                      )}
                      <div className="mt-1.5 flex flex-wrap gap-1.5">
                        {d.repos.length === 0 ? (
                          <span className="text-xs text-slate-400">no repos</span>
                        ) : (
                          d.repos.map((r) => (
                            <span
                              key={r}
                              className="text-[11px] font-mono bg-[#262420] border border-[#262420] px-1.5 py-0.5 rounded-sm"
                            >
                              {r}
                            </span>
                          ))
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      <button className={btnSecondary} onClick={() => startEdit(d)}>
                        Edit
                      </button>
                      <button
                        className="h-9 px-3 text-sm font-medium text-red-400 bg-transparent border border-red-800/60 hover:bg-red-950/40 rounded-none"
                        onClick={() => remove(d)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>
    </div>
  );
};

export default DomainsPage;
